import type { MutationResult } from '../../api/contract';
import { createAppError } from '../../api/errors';
import { agentFixtures } from './fixtures';
import type { AgentRepository } from './repository';
import type {
  AgentDetail,
  AgentListQuery,
  ChangeAgentStatusInput,
  CreateAgentInput,
  DefaultUplineInput,
  HouseMember,
  HouseMemberInput,
  UpdateAgentInput,
} from './types';

const mockPartnerCurrencies = new Map(
  agentFixtures.map((agent) => [agent.partnerId, agent.defaultCurrencyCode] as const),
);

export interface MockAgentRepositoryOptions {
  readonly mode?: 'data' | 'empty' | 'error';
}

// Stable, human-readable route identifiers resolve to the first fixture without duplicating
// fixture rows or altering list/pagination totals.
const agentDetailAliases: Readonly<Record<string, string>> = {
  'agent-a': agentFixtures[0]!.id,
};

export class MockAgentRepository implements AgentRepository {
  readonly #mode: 'data' | 'empty' | 'error';
  #agents: AgentDetail[];

  constructor(options: MockAgentRepositoryOptions = {}) {
    this.#mode = options.mode ?? 'data';
    this.#agents = this.#mode === 'empty' ? [] : agentFixtures.map(cloneAgent);
  }

  async list(query: AgentListQuery, signal: AbortSignal) {
    this.#assertAvailable(signal);
    const filtered = this.#agents
      .filter((agent) => matches(agent, query))
      .sort((left, right) => compare(left, right, query));
    const start = Math.max(0, query.page - 1) * query.pageSize;
    return {
      items: filtered.slice(start, start + query.pageSize).map(cloneAgent),
      total: filtered.length,
      requestId: 'mock-agents-list',
    };
  }

  async get(agentId: string, signal: AbortSignal): Promise<AgentDetail> {
    this.#assertAvailable(signal);
    return cloneAgent(this.#find(agentId));
  }

  create(input: CreateAgentInput): Promise<MutationResult<AgentDetail>> {
    this.#assertAvailable();
    if (this.#agents.some((agent) => agent.code === input.code.toUpperCase())) {
      return Promise.reject(conflict('Agent code already exists', 'mock-agent-conflict'));
    }
    const inheritedCurrency = mockPartnerCurrencies.get(input.partnerId);
    if (inheritedCurrency === undefined) {
      return Promise.reject(notFound('Partner not found', 'mock-agent-partner-not-found'));
    }
    const timestamp = '2026-07-23T12:00:00+07:00';
    const created: AgentDetail = {
      id: mockUuid(700 + this.#agents.length),
      partnerId: input.partnerId,
      code: input.code.toUpperCase(),
      name: input.name,
      contact: { ...input.contact },
      status: 'SUSPENDED',
      isCenterAgent: false,
      defaultCurrencyCode: inheritedCurrency,
      defaultUpline: null,
      userCount: 0,
      activeUserCount: 0,
      createdAt: timestamp,
      createdBy: mockActor,
      updatedAt: timestamp,
      updatedBy: mockActor,
    };
    this.#agents = [created, ...this.#agents];
    return Promise.resolve(result(created, 'mock-agent-create'));
  }

  update(agentId: string, input: UpdateAgentInput): Promise<MutationResult<AgentDetail>> {
    this.#assertAvailable();
    const current = this.#find(agentId);
    const updated: AgentDetail = {
      ...current,
      ...(input.name === undefined ? {} : { name: input.name }),
      ...(input.contact === undefined ? {} : { contact: { ...input.contact } }),
      updatedAt: '2026-07-23T12:10:00+07:00',
      updatedBy: mockActor,
    };
    this.#replace(updated);
    return Promise.resolve(result(updated, 'mock-agent-update'));
  }

  setDefaultUpline(
    agentId: string,
    input: DefaultUplineInput,
  ): Promise<MutationResult<AgentDetail>> {
    this.#assertAvailable();
    const current = this.#find(agentId);
    const updated: AgentDetail = {
      ...current,
      defaultUpline: { memberId: input.memberId, username: null, referralCode: null },
      updatedAt: '2026-07-23T12:20:00+07:00',
      updatedBy: mockActor,
    };
    this.#replace(updated);
    return Promise.resolve(result(updated, 'mock-agent-upline'));
  }

  createHouseMember(
    agentId: string,
    input: HouseMemberInput,
  ): Promise<MutationResult<HouseMember>> {
    this.#assertAvailable();
    const agent = this.#find(agentId);
    const timestamp = '2026-07-23T12:30:00+07:00';
    return Promise.resolve({
      data: {
        id: mockUuid(800 + this.#agents.length),
        partnerId: agent.partnerId,
        partnerUserId: null,
        memberType: 'HOUSE',
        status: 'ACTIVE',
        username: input.username,
        activeReferralCodes: ['REF_01HJKMNPQRSTVWXY'],
        currentAssignment: null,
        defaultWallet: null,
        directDownlineCount: 0,
        isAgentDefaultUpline: false,
        registeredAt: timestamp,
        createdAt: timestamp,
        createdBy: mockActor,
        updatedAt: timestamp,
        updatedBy: mockActor,
      },
      requestId: 'mock-house-member-create',
    });
  }

  changeStatus(
    agentId: string,
    input: ChangeAgentStatusInput,
  ): Promise<MutationResult<AgentDetail>> {
    this.#assertAvailable();
    const current = this.#find(agentId);
    if (current.status === 'CLOSED' && input.status !== 'CLOSED') {
      return Promise.reject(conflict('Closed Agents cannot be reopened', 'mock-agent-status'));
    }
    if (input.status !== 'ACTIVE' && current.activeUserCount > 0) {
      return Promise.reject(
        conflict('Agent has active NORMAL users', 'mock-agent-status-active-users'),
      );
    }
    const updated: AgentDetail = {
      ...current,
      status: input.status,
      updatedAt: '2026-07-23T12:40:00+07:00',
      updatedBy: mockActor,
    };
    this.#replace(updated);
    return Promise.resolve(result(updated, 'mock-agent-status'));
  }

  #assertAvailable(signal?: AbortSignal): void {
    if (signal?.aborted === true) throw new DOMException('Agent request aborted', 'AbortError');
    if (this.#mode === 'error') {
      throw createAppError({
        status: 503,
        code: 'MOCK_AGENTS_UNAVAILABLE',
        message: 'Agent fixture is unavailable',
        requestId: 'mock-agents-error',
        fieldErrors: {},
        retryAfterSeconds: null,
      });
    }
  }

  #find(agentId: string): AgentDetail {
    const resolvedId = agentDetailAliases[agentId] ?? agentId;
    const agent = this.#agents.find((candidate) => candidate.id === resolvedId);
    if (agent === undefined) {
      throw createAppError({
        status: 404,
        code: 'AGENT_NOT_FOUND',
        message: 'Agent not found',
        requestId: 'mock-agent-not-found',
        fieldErrors: {},
        retryAfterSeconds: null,
      });
    }
    return agent;
  }

  #replace(agent: AgentDetail): void {
    this.#agents = this.#agents.map((candidate) => (candidate.id === agent.id ? agent : candidate));
  }
}

function cloneAgent(agent: AgentDetail): AgentDetail {
  return {
    ...agent,
    contact: { ...agent.contact },
    defaultUpline: agent.defaultUpline === null ? null : { ...agent.defaultUpline },
  };
}

function matches(agent: AgentDetail, query: AgentListQuery): boolean {
  return (
    includes(agent.partnerId, query.filters.partnerId) &&
    includes(agent.code, query.filters.code) &&
    includes(agent.name, query.filters.name) &&
    (query.filters.status === '' || agent.status === query.filters.status)
  );
}

function compare(left: AgentDetail, right: AgentDetail, query: AgentListQuery): number {
  const order = String(left[query.sort]).localeCompare(String(right[query.sort]));
  return query.order === 'asc' ? order : -order;
}

function includes(value: string, search: string): boolean {
  return value.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase());
}

function result<T>(data: T, requestId: string): MutationResult<T> {
  return { data, requestId };
}

function conflict(message: string, requestId: string) {
  return createAppError({
    status: 409,
    code: 'AGENT_CONFLICT',
    message,
    requestId,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}

function notFound(message: string, requestId: string) {
  return createAppError({
    status: 404,
    code: 'AGENT_NOT_FOUND',
    message,
    requestId,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}

const mockActor = '00000000-0000-4000-8000-000000000001';

function mockUuid(sequence: number): string {
  return `00000000-0000-4000-8000-${String(sequence).padStart(12, '0')}`;
}
